import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Video, Plus, Edit, Trash2, Eye, EyeOff, Search, RefreshCcw,
  Play, Clock, Users, BarChart2, Settings
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VideoInterviewManagement = () => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInterview, setEditingInterview] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, categories: 0 });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    difficulty: 'beginner',
    duration_minutes: 15,
    questions: [],
    is_active: true
  });

  useEffect(() => {
    loadInterviews();
    loadStats();
  }, []);

  const loadInterviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/video-interviews`, { withCredentials: true });
      setInterviews(response.data.interviews || []);
    } catch (error) {
      // Demo data if API not ready
      setInterviews([
        { id: '1', title: 'Genel Mülakat Hazırlık', category: 'general', difficulty: 'beginner', duration_minutes: 15, is_active: true, completion_count: 45 },
        { id: '2', title: 'THY Mülakat Simülasyonu', category: 'airline', difficulty: 'intermediate', duration_minutes: 30, is_active: true, completion_count: 32 },
        { id: '3', title: 'İngilizce Mülakat Pratik', category: 'english', difficulty: 'advanced', duration_minutes: 25, is_active: false, completion_count: 18 }
      ]);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/video-interviews/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      setStats({ total: 3, active: 2, categories: 3 });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInterview) {
        await axios.put(`${API}/admin/video-interviews/${editingInterview.id}`, formData, { withCredentials: true });
        toast.success('Video mülakat güncellendi');
      } else {
        await axios.post(`${API}/admin/video-interviews`, formData, { withCredentials: true });
        toast.success('Video mülakat oluşturuldu');
      }
      setShowModal(false);
      resetForm();
      loadInterviews();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu video mülakatı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/video-interviews/${id}`, { withCredentials: true });
      toast.success('Video mülakat silindi');
      loadInterviews();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/video-interviews/${id}/toggle`, {}, { withCredentials: true });
      toast.success(currentStatus ? 'Pasif yapıldı' : 'Aktif yapıldı');
      loadInterviews();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'general',
      difficulty: 'beginner',
      duration_minutes: 15,
      questions: [],
      is_active: true
    });
    setEditingInterview(null);
  };

  const handleEdit = (interview) => {
    setEditingInterview(interview);
    setFormData({
      title: interview.title || '',
      description: interview.description || '',
      category: interview.category || 'general',
      difficulty: interview.difficulty || 'beginner',
      duration_minutes: interview.duration_minutes || 15,
      questions: interview.questions || [],
      is_active: interview.is_active
    });
    setShowModal(true);
  };

  const getCategoryBadge = (category) => {
    const badges = {
      'general': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Genel' },
      'airline': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Havayolu' },
      'english': { bg: 'bg-green-100', text: 'text-green-700', label: 'İngilizce' },
      'behavioral': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Davranışsal' }
    };
    return badges[category] || badges['general'];
  };

  const getDifficultyBadge = (difficulty) => {
    const badges = {
      'beginner': { bg: 'bg-green-100', text: 'text-green-700', label: 'Başlangıç' },
      'intermediate': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Orta' },
      'advanced': { bg: 'bg-red-100', text: 'text-red-700', label: 'İleri' }
    };
    return badges[difficulty] || badges['beginner'];
  };

  const filteredInterviews = interviews.filter(i => 
    i.title?.toLowerCase().includes(search.toLowerCase()) ||
    i.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-600 rounded-lg text-white">
            <Video className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Video Mülakat Yönetimi</h2>
            <p className="text-sm text-gray-500">{stats.total} mülakat, {stats.active} aktif</p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="gap-2 bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4" />
          Yeni Mülakat
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Video className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-700">{stats.total}</p>
                <p className="text-xs text-purple-600">Toplam Mülakat</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Play className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.active}</p>
                <p className="text-xs text-green-600">Aktif Mülakat</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BarChart2 className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.categories}</p>
                <p className="text-xs text-blue-600">Kategori</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">
                  {interviews.reduce((sum, i) => sum + (i.completion_count || 0), 0)}
                </p>
                <p className="text-xs text-amber-600">Toplam Tamamlama</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="border-0 shadow">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Mülakat ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={loadInterviews}>
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Interviews List */}
      <Card className="border-0 shadow">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCcw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : filteredInterviews.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Video className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Henüz video mülakat yok</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredInterviews.map((interview) => {
                const catBadge = getCategoryBadge(interview.category);
                const diffBadge = getDifficultyBadge(interview.difficulty);
                return (
                  <div key={interview.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-medium ${interview.is_active ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                            {interview.title}
                          </h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${catBadge.bg} ${catBadge.text}`}>
                            {catBadge.label}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${diffBadge.bg} ${diffBadge.text}`}>
                            {diffBadge.label}
                          </span>
                          {!interview.is_active && (
                            <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Pasif</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {interview.duration_minutes} dk
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {interview.completion_count || 0} tamamlama
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleToggleActive(interview.id, interview.is_active)}>
                          {interview.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(interview)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(interview.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editingInterview ? 'Mülakatı Düzenle' : 'Yeni Video Mülakat'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Başlık *</label>
                  <Input
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Mülakat başlığı"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Açıklama</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full p-2 border rounded-md resize-none h-20"
                    placeholder="Mülakat açıklaması..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Kategori</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full h-10 px-3 border rounded-md"
                    >
                      <option value="general">Genel</option>
                      <option value="airline">Havayolu</option>
                      <option value="english">İngilizce</option>
                      <option value="behavioral">Davranışsal</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Zorluk</label>
                    <select
                      value={formData.difficulty}
                      onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                      className="w-full h-10 px-3 border rounded-md"
                    >
                      <option value="beginner">Başlangıç</option>
                      <option value="intermediate">Orta</option>
                      <option value="advanced">İleri</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Süre (dakika)</label>
                  <Input
                    type="number"
                    min="5"
                    max="60"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                    İptal
                  </Button>
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                    {editingInterview ? 'Güncelle' : 'Oluştur'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default VideoInterviewManagement;
